import type { Constructor, Schema, SerializedWorld, SystemImpl, WorldComponent } from "minecs";
import { applyHotReload, getHotSystem, hasHotSystems, registerHotWorld, stepHotWorld } from "../dev/hotReload";
import { forgetEntityTemplate } from "../entity/hotDefaults";
import {
  addComponent,
  addEntity,
  createWorld,
  defineQuery,
  entityExists,
  getComponentByType,
  hasComponent,
  stepWorld,
  stepWorldTiming,
  componentList,
  type World,
  getSystemsByType,
  removeComponent,
  getSystem,
  removeEntity,
  stepWorldDraw,
  serializeWorld,
  deserializeWorld,
  SerialMode,
  deleteWorld,
  StringToEnum,
} from "minecs";
import { Random } from "yage/schemas/core/Random";
import type { Random as RandomType } from "yage/utils/rand";
import "yage/systems";
import type { PhysicsSaveState } from "yage/systems/physics/Physics";
import { PhysicsSystem } from "yage/systems/physics/Physics";
import { MapSystem } from "yage/systems/map/Map";
import Description from "yage/schemas/core/Description";
import { flags } from "yage/console/flags";
import { EntityFactory } from "yage/entity/EntityFactory";
import { EntityTypeEnum } from "yage/constants/enums";
import { ComponentCategory } from "yage/constants/enums";
import type { ComponentData } from "yage/systems/types";
import { EntityType } from "yage/schemas/entity/Types";
import { Parent } from "yage/schemas/entity/Parent";
import type { InputManager } from "yage/inputs/InputManager";
import type { PlayerEventManager } from "yage/inputs/PlayerEventManager";
import { ShareOnEject } from "yage/schemas/share/ShareOnEject";
import { DoNotEject } from "yage/schemas/entity/DoNotEject";
import { UiCleanupOnLeave } from "yage/schemas/render/UiCleanupOnLeave";
import { FIXED_TIMESTEP_MS } from "yage/game/Timing";

// @ts-expect-error - MineCS doesn't have a type for this
type EntityWithComponent<T extends Schema> = number & { __hasComponent: T["type"] };

export type GameModelState = {
  core: number;
  timeElapsed: number;
  frame: number;
  world: SerializedWorld | string;
  jsonWorld?: SerializedWorld;
  physics?: PhysicsSaveState;
};

export type EjectedEntity = {
  entityType: string | EntityTypeEnum;
  description: string;
  entityId: number;
  components: ComponentData[];
  entities: number[];
  children: { [key: number]: EjectedEntity };
  hasChildren: boolean;
};

export type GameModel = World & {
  inputManager: InputManager;
  roomId: string;
  seed: string;
  ping: number;
  timeElapsed: number;
  rand: RandomType;
  coreEntity: number;
  players: number[];
  paused: boolean;
  preloadOnly: boolean;
  destroyed: boolean;
  localNetIds: string[];
  currentWorld: number;
  frameDt: number;
  executionMode: "realtime" | "step";
  event: (netId: string, event: string, data: any) => void;
  step: (dt?: number) => void;
  stepDraw: () => void;
  getTypedUnsafe: <T extends Schema>(type: Constructor<T>, entity: number) => T;
  getTyped: {
    <T extends Schema>(type: Constructor<T>, entity: EntityWithComponent<T>): T;
    <T extends Schema>(type: Constructor<T>, entity: number): T | null;
  };
  hasComponent: <T extends Schema>(type: Constructor<T> | string, entity: number) => entity is EntityWithComponent<T>;
  getComponent: <T extends Schema>(type: Constructor<T> | string, entity: number) => T | any | null;
  cloneComponent: <T extends Schema>(type: Constructor<T> | string, entity: number) => ComponentData | null;
  ejectComponent: <T extends Schema>(type: Constructor<T> | string, entity: number) => ComponentData | null;
  ejectEntity: (entity: number) => EjectedEntity;
  cloneEntity: (entity: number) => EjectedEntity;
  injectEntity: (entity: EjectedEntity) => number;
  addComponent: <T extends Schema>(
    type: Constructor<T> | string,
    entity: number,
    overrides?: Partial<T>,
    reset?: boolean
  ) => void;
  getComponentActives: (type: string | typeof Schema) => number[];
  getCategory: (type: string | typeof Schema) => ComponentCategory;
  getComponentsByCategory: (category: number, entity?: number) => (typeof Schema)[];
  removeComponent: <T extends Schema>(type: Constructor<T> | string, entity: number) => void;
  isActive: (entity: number) => boolean;
  getSystemsByType: (type: string, entity?: number) => SystemImpl<any>[];
  getSystem: <T extends typeof SystemImpl<any>>(system: T) => InstanceType<T>;
  addEntity: () => number;
  removeEntity: (entity: number) => void;
  serializeState: () => GameModelState;
  deserializeState: (state: GameModelState) => Promise<void>;
  destroy: () => void;
  getEntityByDescription: (description: string) => number[] | undefined;
  getEntityByType(type: EntityTypeEnum | EntityTypeEnum[]): number[];
  logEntity: (entity: number, debugOverride?: boolean) => void;
  runGlobalMods: (
    category: ComponentCategory,
    overrides?: {
      [key: string]: any;
    }
  ) => void;
  runMods: (
    entity: number | number[],
    category: ComponentCategory,
    overrides?: {
      [key: string]: any;
    },
    after?: (
      system: SystemImpl<GameModel>,
      components: Schema[],
      overrides: { [key: string]: any },
      isLast: boolean
    ) => void | boolean
  ) => void;
};

const MAX_ENTITY_DIAGNOSTIC_ROW_LIMIT = 50;

const isMaxEntityError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error);
  return /max entities|max entity|entity limit|entity capacity|maximum.*entities|no more entities/i.test(message);
};

const incrementGroup = <T extends { count: number; examples: string[] }>(
  groups: Map<string, T>,
  key: string,
  create: () => T,
  example: string
) => {
  const group = groups.get(key) ?? create();
  group.count += 1;
  if (group.examples.length < 8) {
    group.examples.push(example);
  }
  groups.set(key, group);
};

const sortedRows = <T extends { count: number }>(rows: T[]) => rows.sort((a, b) => b.count - a.count);

// Lockstep peers must serialize the same event data to the same string:
// plain JSON.stringify depends on object key insertion order, which is an
// implementation detail of whichever code built the object.
const canonicalStringify = (value: any): string => {
  if (value === null || typeof value !== "object") {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map(canonicalStringify).join(",")}]`;
  }
  const keys = Object.keys(value).sort();
  return `{${keys.map((key) => `${JSON.stringify(key)}:${canonicalStringify(value[key])}`).join(",")}}`;
};

const tableOrLog = (label: string, rows: Record<string, unknown>[]) => {
  console.error(label);
  if (typeof console.table === "function") {
    console.table(rows);
  } else {
    console.error(rows);
  }
};

const entityComponentSet = (world: World, entity: number): string[] => {
  return componentList
    .filter((component) => hasComponent(world, component, entity))
    .map((component) => component.type)
    .sort();
};

const logMaxEntityDiagnostics = (world: World, error: unknown) => {
  if (!isMaxEntityError(error)) {
    return;
  }

  const activeEntities = [...world.entitySparseSet.dense].sort((a, b) => a - b);
  const byDescription = new Map<string, { count: number; examples: string[] }>();
  const withoutDescriptionByComponentSet = new Map<
    string,
    { count: number; componentCount: number; components: string; examples: string[] }
  >();

  activeEntities.forEach((entity) => {
    if (hasComponent(world, Description, entity)) {
      const description = world(Description, entity).description || "(blank Description)";
      incrementGroup(
        byDescription,
        description,
        () => ({ count: 0, examples: [] }),
        `#${entity}`
      );
      return;
    }
    const components = entityComponentSet(world, entity);
    const componentKey = components.join(" + ") || "(no components)";
    incrementGroup(
      withoutDescriptionByComponentSet,
      componentKey,
      () => ({ count: 0, componentCount: components.length, components: componentKey, examples: [] }),
      `#${entity}`
    );
  });

  const descriptionRows = sortedRows(
    Array.from(byDescription.entries()).map(([description, group]) => ({
      count: group.count,
      description,
      examples: group.examples.join(", "),
    }))
  );
  const componentRows = sortedRows(
    Array.from(withoutDescriptionByComponentSet.values()).map((group) => ({
      count: group.count,
      componentCount: group.componentCount,
      components: group.components,
      examples: group.examples.join(", "),
    }))
  );
  const visibleDescriptionRows = descriptionRows.slice(0, MAX_ENTITY_DIAGNOSTIC_ROW_LIMIT);
  const visibleComponentRows = componentRows.slice(0, MAX_ENTITY_DIAGNOSTIC_ROW_LIMIT);

  console.error(
    `[Yage] max entities reached. active=${activeEntities.length}/${world.size}, cursor=${world.entityCursor}, reusableRemoved=${
      world.removed.length
    }`
  );
  tableOrLog(
    `[Yage] active entities by Description (showing ${visibleDescriptionRows.length}/${descriptionRows.length})`,
    visibleDescriptionRows
  );
  tableOrLog(
    `[Yage] active entities without Description by component set (showing ${visibleComponentRows.length}/${componentRows.length})`,
    visibleComponentRows
  );
};

const publishPerformanceTiming = (timing: World["timing"], channel: "perf" | "draw") => {
  if (!timing || typeof window === "undefined" || !(window as any).performanceUpdate) {
    return;
  }
  (window as any).performanceUpdate(
    timing.systems.map((system) => ({ type: system.name, time: system.totalTime })),
    channel
  );
};

const drawSystemEnabled = (systemName: string): boolean => {
  if (typeof window === "undefined") {
    return true;
  }

  const checker = (window as Window & { yageDrawSystemEnabled?: (systemName: string) => boolean })
    .yageDrawSystemEnabled;
  return typeof checker === "function" ? checker(systemName) !== false : true;
};

const shouldScheduledSystemRun = (frame: number, frameMod: number, frameModOffset: number): boolean => {
  if (frameMod <= 1 && frameModOffset === 0) return true;
  const clampedMod = Math.max(1, Math.min(60, frameMod));
  const clampedOffset = Math.max(0, Math.min(clampedMod - 1, frameModOffset));
  return (frame - 1 - clampedOffset) % clampedMod === 0 && frame > clampedOffset;
};

const drawSystemCleanupState = new WeakMap<World, Map<string, Set<number>>>();
const drawSystemCleanupSkipped = new Set(["PixiViewportSystem"]);

const cleanupStateForDrawSystem = (world: World, systemName: string): Set<number> => {
  let worldState = drawSystemCleanupState.get(world);
  if (!worldState) {
    worldState = new Map();
    drawSystemCleanupState.set(world, worldState);
  }
  let cleanedEntities = worldState.get(systemName);
  if (!cleanedEntities) {
    cleanedEntities = new Set();
    worldState.set(systemName, cleanedEntities);
  }
  return cleanedEntities;
};

const cleanupDisabledDrawSystem = (world: World, system: SystemImpl<any>, systemName: string, entities: number[]) => {
  if (drawSystemCleanupSkipped.has(systemName) || typeof system.cleanup !== "function") {
    return;
  }

  const cleanedEntities = cleanupStateForDrawSystem(world, systemName);
  for (const entity of entities) {
    if (cleanedEntities.has(entity)) {
      continue;
    }
    system.cleanup(world, entity);
    cleanedEntities.add(entity);
  }
};

const restoreEnabledDrawSystem = (world: World, system: SystemImpl<any>, systemName: string, entities: number[]) => {
  const cleanedEntities = drawSystemCleanupState.get(world)?.get(systemName);
  if (!cleanedEntities?.size) {
    return;
  }

  if (typeof system.init === "function") {
    for (const entity of entities) {
      if (cleanedEntities.has(entity)) {
        system.init(world, entity);
      }
    }
  }
  cleanedEntities.clear();
  drawSystemCleanupState.get(world)?.delete(systemName);
};

const clonePlainData = <T>(value: T): T => {
  if (value === undefined || value === null || typeof value !== "object") {
    return value;
  }
  if (typeof globalThis.structuredClone === "function") {
    return globalThis.structuredClone(value);
  }
  return JSON.parse(JSON.stringify(value)) as T;
};

const stepWorldDrawTiming = (world: World): World["timing"] => {
  const timing: NonNullable<World["timing"]> = { systems: [], totalTime: 0 };
  const systems = world.drawSystems;
  for (let i = 0; i < systems.length; i++) {
    const system = systems[i];
    const SystemClass = system.constructor as typeof SystemImpl;
    if (!shouldScheduledSystemRun(world.frame, SystemClass.frameMod ?? 1, SystemClass.frameModOffset ?? 0)) {
      continue;
    }
    const systemName = system.constructor.name;
    const entities = system.query(world);
    if (!entities.length) {
      continue;
    }

    if (!drawSystemEnabled(systemName)) {
      cleanupDisabledDrawSystem(world, system, systemName, entities);
      timing.systems.push({
        name: systemName,
        totalTime: 0,
        totalCalls: entities.length,
        averageTime: 0,
      });
      continue;
    }

    restoreEnabledDrawSystem(world, system, systemName, entities);
    const start = performance.now();
    system.runAll(world);
    const elapsed = performance.now() - start;
    timing.totalTime += elapsed;
    timing.systems.push({
      name: systemName,
      totalTime: elapsed,
      totalCalls: entities.length,
      averageTime: elapsed / Math.max(1, entities.length),
    });
  }
  return timing;
};

export const GameModel = ({
  world = createWorld(10000),
  seed,
  roomId,
  inputManager,
  playerEventManager,
}: {
  world?: World;
  seed?: string;
  roomId: string;
  inputManager: InputManager;
  playerEventManager: PlayerEventManager;
}): GameModel => {
  registerHotWorld(world);
  const allocateEntity = () => {
    try {
      const entity = addEntity(world);
      forgetEntityTemplate(world, entity);
      return entity;
    } catch (error) {
      logMaxEntityDiagnostics(world, error);
      throw error;
    }
  };
  const componentsByCategory = componentList.reduce((acc, component) => {
    acc[component.category] = acc[component.category] || [];
    acc[component.category].push(component);
    return acc;
  }, {} as Record<number, (typeof Schema)[]>);
  let sortedSystemsByCategory: Record<number, SystemImpl<any>[]>;
  let sortedSystemsByComponent: Record<string, SystemImpl<any>[]>;
  let componentsBySystem: Record<string, string[]>;
  const refreshSystemLookups = () => {
    sortedSystemsByCategory = Object.entries(componentsByCategory).reduce((acc, [category, components]) => {
      acc[parseInt(category)] = components
        .map((component) =>
          getSystemsByType(world, component.type).sort(
            (a, b) => (a.constructor as typeof SystemImpl).depth - (b.constructor as typeof SystemImpl).depth
          )
        )
        .flat()
        .filter((system, index, array) => array.indexOf(system) === index);
      return acc;
    }, {} as Record<number, SystemImpl<any>[]>);

    sortedSystemsByComponent = componentList.reduce((acc, component) => {
      acc[component.type] = getSystemsByType(world, component.type).sort(
        (a, b) => (a.constructor as typeof SystemImpl).depth - (b.constructor as typeof SystemImpl).depth
      );
      return acc;
    }, {} as Record<string, SystemImpl<any>[]>);

    componentsBySystem = Object.entries(sortedSystemsByComponent).reduce((acc, [type, systems]) => {
      systems.flat().forEach((system) => {
        acc[system.constructor.name] = acc[system.constructor.name] || [];
        acc[system.constructor.name].push(type);
      });
      return acc;
    }, {} as Record<string, string[]>);
  };
  refreshSystemLookups();

  const gameModel = Object.assign(world, {
    inputManager,
    roomId: roomId ?? "",
    seed: seed ?? "",
    coreEntity: -1,
    timeElapsed: 0,
    rand: null as any,
    ping: 0,
    frameDt: FIXED_TIMESTEP_MS,
    paused: false,
    preloadOnly: false,
    destroyed: false,
    players: [] as number[],
    localNetIds: [],
    currentWorld: 0,
    executionMode: "realtime" as "realtime" | "step",
    getEntityByDescription(description: string): number[] | undefined {
      const entities = this.getComponentActives("Description");
      return entities?.filter((entity) => {
        const desc = this.getTypedUnsafe(Description, entity);
        return desc.description === description;
      });
    },
    getCategory(type: string | typeof Schema): ComponentCategory {
      if (typeof type === "string") {
        const schema = getComponentByType(type);
        if (!schema) {
          return ComponentCategory.NONE;
        }
        return schema.category;
      }
      return type.category;
    },
    getEntityByType(type: EntityTypeEnum | EntityTypeEnum[]): number[] {
      const entities = this.getComponentActives("EntityType");
      return entities?.filter((entity) => {
        const entityType = this.getTypedUnsafe(EntityType, entity);
        if (Array.isArray(type)) {
          return type.includes(entityType.entityType);
        }
        return entityType.entityType === type;
      });
    },
    step: (dt?: number) => {
      if (applyHotReload(gameModel)) refreshSystemLookups();
      const elapsed = dt ?? FIXED_TIMESTEP_MS;
      gameModel.timeElapsed += elapsed;
      gameModel.frameDt = elapsed;
      if (hasHotSystems(gameModel)) {
        stepHotWorld(gameModel, flags.PERFORMANCE_LOGS);
        if (flags.PERFORMANCE_LOGS) publishPerformanceTiming(gameModel.timing, "perf");
      } else if (flags.PERFORMANCE_LOGS) {
        stepWorldTiming(gameModel);
        publishPerformanceTiming(gameModel.timing, "perf");
      } else {
        stepWorld(gameModel);
      }
    },
    stepDraw: () => {
      if (applyHotReload(gameModel)) refreshSystemLookups();
      if (flags.DRAW_PERFORMANCE_LOGS) {
        publishPerformanceTiming(stepWorldDrawTiming(gameModel), "draw");
        return;
      }
      stepWorldDraw(gameModel);
    },
    addComponent: <T extends Schema>(
      type: Constructor<T> | string,
      entity: number,
      overrides?: Partial<T>,
      reset?: boolean
    ) => {
      if (typeof type === "string") {
        const schema = getComponentByType(type);
        if (!schema) {
          return;
        }
        addComponent(world, schema as unknown as Constructor<T>, entity, overrides, reset);
      } else {
        addComponent(world, type as unknown as Constructor<T>, entity, overrides, reset);
      }
    },
    getComponent: <T extends Schema>(type: Constructor<T> | string, entity: number): T | null => {
      if (typeof type === "string") {
        const schema = getComponentByType(type);
        if (!schema) {
          return null;
        }
        return world(schema, entity) as T;
      }
      return world(type, entity);
    },
    getTypedUnsafe: <T extends Schema>(type: Constructor<T>, entity: number) => {
      return world(type, entity);
    },
    getTyped: <T extends Schema>(type: Constructor<T>, entity: number | EntityWithComponent<T>): T | null => {
      if (hasComponent(world, type, entity)) {
        return world(type, entity);
      }
      return null;
    },
    event: (netId: string, event: string, data: any) => {
      playerEventManager.addEvent(netId, `${event}::${canonicalStringify(data)}`);
    },
    injectEntity: (entity: EjectedEntity, entityIdMap: { [eid: number]: number } = {}) => {
      const { entityType, description, components, entities } = entity;

      if (Object.keys(entityIdMap).length === 0) {
        for (let i = 0; i < entities.length; i++) {
          entityIdMap[entities[i]] = allocateEntity();
        }
      }

      const newEntity = entityIdMap[entity.entityId];
      if (entityType) {
        gameModel.addComponent(EntityType, newEntity, { entityType: StringToEnum(entityType, EntityTypeEnum) });
      }
      gameModel.addComponent(Description, newEntity, { description });
      components.forEach((component) => {
        const schema = getComponentByType(component.type);
        if (!schema) {
          return;
        }
        const componentData = clonePlainData(component.data);
        if (schema.entityTypes?.length) {
          for (let i = 0; i < schema.entityTypes.length; i++) {
            const key = schema.entityTypes[i];
            if (componentData[key] !== undefined) {
              if (Array.isArray(componentData[key])) {
                componentData[key] = componentData[key]
                  .map((eid: number) => entityIdMap[eid])
                  .filter((eid: number) => typeof eid === "number");
              } else {
                if (entityIdMap[componentData[key]] === undefined) {
                  delete componentData[key];
                } else {
                  componentData[key] = entityIdMap[componentData[key]];
                }
              }
            }
          }
        }
        gameModel.addComponent(component.type, newEntity, componentData);
      });
      if (entity.hasChildren) {
        Object.keys(entity.children).forEach((child) => {
          gameModel.injectEntity(entity.children[child as unknown as number], entityIdMap);
        });
      }
      return newEntity;
    },
    cloneEntity: (entity: number): EjectedEntity => {
      const data = {
        entityType: gameModel(EntityType).store.entityType[entity],
        description: gameModel.getTypedUnsafe(Description, entity)?.description ?? "",
        entityId: entity,
        components: [],
        entities: [],
        children: {},
        hasChildren: false,
      } as EjectedEntity;
      const children = gameModel.hasComponent(Parent, entity) ? gameModel.getTypedUnsafe(Parent, entity).children ?? [] : [];

      for (let i = 0; i < children.length; i++) {
        if (gameModel.hasComponent(DoNotEject, children[i]) || data.children[children[i]]) {
          continue;
        }
        const childEntity = gameModel.cloneEntity(children[i]);

        data.entities.push(...childEntity.entities);
        data.children[childEntity.entityId] = childEntity;
      }

      const components: ComponentData[] = [];
      componentList.forEach((component) => {
        if (hasComponent(world, component, entity)) {
          const componentData = gameModel.cloneComponent(component, entity);
          if (componentData) {
            components.push(componentData);
          }
        }
      });
      data.components = components;
      data.entities.push(entity);
      data.entities = data.entities.filter((e: number) => gameModel.isActive(e)).sort();
      data.hasChildren = !!Object.keys(data.children).length;
      return data;
    },
    ejectEntity: (entity: number, removeEjectedEntity = true): any => {
      if (removeEjectedEntity) {
        gameModel.runMods(
          gameModel.hasComponent(ShareOnEject, entity)
            ? [...(gameModel.getTyped(ShareOnEject, entity)?.entities ?? [])]
            : [entity],
          ComponentCategory.ON_EJECT,
          {
            owner: entity,
          }
        );
      }

      const data = {
        entityType: gameModel(EntityType).store.entityType[entity],
        description: gameModel.getTypedUnsafe(Description, entity)?.description ?? "",
        entityId: entity,
        components: [],
        entities: [],
        children: {},
        hasChildren: false,
      } as EjectedEntity;
      const children = gameModel.getTypedUnsafe(Parent, entity)?.children ?? [];

      for (let i = 0; i < children.length; i++) {
        if (gameModel.hasComponent(DoNotEject, children[i]) || data.children[children[i]]) {
          continue;
        }
        const childEntity = gameModel.ejectEntity(children[i], false);

        data.entities.push(...childEntity.entities);
        data.children[childEntity.entityId] = childEntity;
      }

      const components: ComponentData[] = [];
      componentList.forEach((component) => {
        if (hasComponent(world, component, entity)) {
          const componentData = gameModel.ejectComponent(component, entity);
          if (componentData) {
            components.push(componentData);
          }
        }
      });
      data.components = components;
      data.entities.push(entity);
      data.entities = data.entities.filter((e: number) => gameModel.isActive(e)).sort();
      if (removeEjectedEntity) {
        gameModel.removeEntity(entity);
      }
      data.hasChildren = !!Object.keys(data.children).length;
      return data;
    },
    ejectComponent: <T extends Schema>(type: Constructor<T> | string, entity: number): ComponentData | null => {
      if (typeof type === "string") {
        type = getComponentByType(type) as unknown as Constructor<T>;
        if (!type) {
          return null;
        }
      }
      if (hasComponent(world, type, entity)) {
        const component = world(type, entity);
        // @ts-ignore
        const { type: componentType, ...data } = component;
        // @ts-ignore
        if (component.ejecting !== undefined) {
          // @ts-ignore
          component.ejecting = true;
        }
        gameModel.removeComponent(type, entity);
        return {
          type: componentType,
          data,
        };
      }
      return null;
    },
    cloneComponent: <T extends Schema>(type: Constructor<T> | string, entity: number): ComponentData | null => {
      if (typeof type === "string") {
        type = getComponentByType(type) as unknown as Constructor<T>;
        if (!type) {
          return null;
        }
      }
      if (hasComponent(world, type, entity)) {
        const component = world(type, entity);
        // @ts-ignore
        const { type: componentType, ...data } = component;
        return {
          type: componentType,
          data: clonePlainData(data),
        };
      }
      return null;
    },
    removeComponent: <T extends Schema>(type: Constructor<T> | string, entity: number) => {
      if (typeof type === "string") {
        const schema = getComponentByType(type);
        if (!schema) {
          return;
        }
        removeComponent(world, schema, entity);
      } else {
        removeComponent(world, type as unknown as typeof Schema, entity);
      }
    },
    getSystemsByType: <T extends Schema>(type: Constructor<T> | string, entity?: number) => {
      if (typeof type !== "string") {
        type = type.name;
      }
      if (entity === undefined) {
        return sortedSystemsByComponent[type] || [];
      }
      return sortedSystemsByComponent[type].filter((system) => system.query.has(gameModel, entity));
    },
    runGlobalMods: (
      category: ComponentCategory,
      overrides?: {
        [key: string]: any;
      }
    ) => {
      const modComponents = gameModel.getComponentsByCategory(category);
      overrides = overrides || {};
      const sortedSystems: [SystemImpl, typeof Schema][] = [];
      const overrideKeys = Object.keys(overrides);

      if (modComponents.length) {
        modComponents.forEach((onEndComponent) => {
          const systems = gameModel.getSystemsByType(onEndComponent.type);
          if (systems.length) {
            systems.forEach((system: SystemImpl | SystemImpl[]) => {
              if (!Array.isArray(system)) {
                system = [system];
              }
              system.forEach((s) => {
                if (!sortedSystems.find((ss) => ss[0] === s)) {
                  sortedSystems.push([s, onEndComponent]);
                }
              });
            });
          }
        });
        sortedSystems.sort((a, b) => {
          return (a[0].constructor as typeof SystemImpl).depth - (b[0].constructor as typeof SystemImpl).depth;
        });

        sortedSystems.forEach(([system, component]) => {
          const actives = gameModel.getComponentActives(component.type);
          actives.forEach((entity) => {
            if (overrideKeys.length > 0) {
              const componentData = gameModel.getComponent(component, entity) as any;

              if (componentData) {
                for (const key of overrideKeys) {
                  if (componentData[key] !== undefined) {
                    componentData[key] = overrides[key];
                  }
                }
              }
            }

            system.run?.(gameModel, entity);
          });
        });
      }
    },
    runMods: (
      entity: number | number[],
      category: ComponentCategory,
      overrides?: {
        [key: string]: any;
      },
      after?: (
        system: SystemImpl<GameModel>,
        components: Schema[],
        overrides: { [key: string]: any },
        isLast: boolean
      ) => void | boolean
    ) => {
      const systemGroups = sortedSystemsByCategory[category];
      if (!systemGroups || entity === undefined) {
        return;
      }
      overrides = overrides || {};
      const targets = [...new Set(Array.isArray(entity) ? entity : [entity])];
      if (!targets.length) {
        return;
      }
      const overrideKeys = Object.keys(overrides);
      // Manual (negative-depth) systems only, depth-ordered. Per-frame systems
      // run through stepWorld instead.
      const systems = [...new Set(systemGroups.flat())]
        .filter((system) => (system.constructor as typeof SystemImpl).depth < 0)
        .sort((a, b) => (a.constructor as typeof SystemImpl).depth - (b.constructor as typeof SystemImpl).depth);

      for (let i = 0; i < systems.length; i++) {
        const system = systems[i];
        let entities = targets;
        entityLoop: for (let k = 0; k < entities.length; k++) {
          const target = entities[k];
          const components: Schema[] = [];
          if (overrideKeys.length > 0) {
            const systemComponents = componentsBySystem[system.constructor.name];
            for (const type of systemComponents) {
              const schema = getComponentByType(type);
              if (schema && !gameModel.hasComponent(schema, target)) {
                continue entityLoop;
              }
              if (schema?.category === category) {
                const component = gameModel.getComponent(schema, target) as any;
                components.push(component);
                if (component) {
                  for (const key of overrideKeys) {
                    if (component[key] !== undefined) {
                      component[key] = overrides[key];
                    }
                  }
                }
              }
            }
          }
          system.run?.(gameModel, target);
          if (after?.(system, components, overrides, i === systems.length - 1 && k === entities.length - 1) === false) {
            entities = entities.slice(k + 1);
            k = -1;
          }
        }
      }
    },
    hasComponent: <T extends Schema>(
      type: Constructor<T> | string,
      entity: number
    ): entity is EntityWithComponent<T> => {
      if (typeof type === "string") {
        const schema = getComponentByType(type);
        if (!schema) {
          return false;
        }
        return hasComponent(world, schema, entity);
      }

      return hasComponent(world, type, entity);
    },
    getComponentActives: (type: string | typeof Schema) => {
      const component = type instanceof Function ? type : getComponentByType(type);
      if (!component) {
        return [];
      }
      return defineQuery([component])(world);
    },
    isActive: (entity: number) => {
      return entityExists(world, entity);
    },
    getComponentsByCategory: (category: number, entity?: number) => {
      if (entity !== undefined) {
        return componentList.filter(
          (component) => component.category === category && hasComponent(world, component, entity)
        );
      }
      return componentsByCategory[category] || [];
    },
    getSystem: <T extends typeof SystemImpl<any>>(system: T) => {
      return getHotSystem(world, system) ?? getSystem(world, system);
    },
    addEntity: () => {
      return allocateEntity();
    },
    removeEntity: (entity: number) => {
      if (
        entityExists(world, entity) &&
        gameModel.hasComponent("Physics", gameModel.coreEntity) &&
        (gameModel.hasComponent("RigidCircle", entity) || gameModel.hasComponent("RigidBox", entity))
      ) {
        gameModel.getSystem(PhysicsSystem).removeEntity(gameModel, entity);
      }
      if (entityExists(world, entity) && gameModel.hasComponent("Map", entity)) {
        gameModel.getSystem(MapSystem).cleanup(gameModel, entity);
      }
      removeEntity(world, entity);
    },
    serializeState(): GameModelState {
      const hasPhysics = gameModel.hasComponent("Physics", gameModel.coreEntity);
      const physicsSystem = hasPhysics ? this.getSystem(PhysicsSystem) : null;

      return {
        core: gameModel.coreEntity,
        timeElapsed: gameModel.timeElapsed,
        frame: gameModel.frame,
        world: flags.SERIALIZE_TO_BUFFER
          ? serializeWorld(SerialMode.BASE64, world)
          : serializeWorld(SerialMode.JSON, world),
        jsonWorld: flags.SERIALIZE_TO_BUFFER ? serializeWorld(SerialMode.JSON, world) : undefined,
        physics: physicsSystem?.save(),
      };
    },
    deserializeState: async (state: GameModelState) => {
      gameModel.coreEntity = state.core;
      gameModel.timeElapsed = state.timeElapsed;
      gameModel.frame = state.frame;

      deleteWorld(world);

      deserializeWorld(state.world, world);

      if (state.physics && gameModel.hasComponent("Physics", state.core)) {
        const physicsSystem = getSystem(gameModel, PhysicsSystem);
        physicsSystem.init(gameModel, state.core);
        physicsSystem.getEngine?.(gameModel);
        await physicsSystem.restore(state.physics);
      }
    },
    destroy: () => {
      if (gameModel.destroyed) {
        return;
      }
      if (gameModel.hasComponent("Physics", gameModel.coreEntity)) {
        gameModel.getSystem(PhysicsSystem).cleanup?.();
      }
      const entities = [...world.entitySparseSet.dense].sort((a, b) => b - a);
      for (const entity of entities) {
        if (entityExists(world, entity)) {
          gameModel.removeEntity(entity);
        }
      }
      deleteWorld(world);
      gameModel.destroyed = true;
    },
    logEntity: (entity: number, debugOverride?: boolean) => {
      if (!debugOverride && !flags.DEBUG) {
        return;
      }
      const entityData: any = {
        id: entity,
        name: gameModel.getTyped(Description, entity)?.description ?? "",
        components: {},
      };
      componentList.forEach((component) => {
        if (hasComponent(world, component, entity)) {
          entityData.components[component.name] = { ...world(component, entity) };
        }
      });
      console.trace(entityData);
    },
  });

  if (world.frame === 0) {
    gameModel.coreEntity = EntityFactory.getInstance().generateEntity(gameModel, "core");
    if (!gameModel.hasComponent(UiCleanupOnLeave, gameModel.coreEntity)) {
      gameModel.addComponent(UiCleanupOnLeave, gameModel.coreEntity);
    }
    addComponent(gameModel, Random, gameModel.coreEntity, { seed: seed ?? "" });
  }

  Object.defineProperty(gameModel, "players", {
    get: () => gameModel.getComponentActives("PlayerType"),
  });

  return gameModel;
};

export interface ReadOnlyGameModel extends GameModel {
  readonly: true;
  <T extends Schema>(schema: Constructor<T>, eid: number): Readonly<T>;
  <T extends Schema>(schema: Constructor<T>): Readonly<WorldComponent<T>>;
  getComponent: <T extends Schema>(type: Constructor<T> | string, entity: number) => Readonly<T> | null;
}
